import React, { useState, useRef, useEffect } from 'react'
import PropTypes from 'prop-types'
import { useNavigate } from 'react-router-dom'
import { TrendingUp, Globe, Calendar, ExternalLink, Copy, Check, Image as ImageIcon, ChevronLeft, ChevronRight, RefreshCw, BarChart3, AlertTriangle, X, Clock, Tag } from 'lucide-react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import CitationPopup from '../../components/CitationPopup'
import AgentHeader from '../../components/AgentHeader'

const TrendingResultPage = ({ query, trendingResult, dateRange, onBack, onRetry }) => {
  const navigate = useNavigate()
  const [copiedText, setCopiedText] = useState('')
  const [showCitation, setShowCitation] = useState(false)
  const [selectedCitation, setSelectedCitation] = useState(null)
  const [citationPosition, setCitationPosition] = useState({ x: 0, y: 0 })
  const [showAllSources, setShowAllSources] = useState(false)
  const [showAllImages, setShowAllImages] = useState(false)
  const sourcesScrollRef = useRef(null)
  const imagesScrollRef = useRef(null)

  // Handle navigation to trending agent
  const handleSearchMore = () => {
    navigate('/', { state: { selectedAgentId: 'trending_agent' } })
  }

  // Handle citation click
  const handleCitationClick = (event, citationNumber) => {
    event.preventDefault()
    const source = trendingResult.sources.find(s => s.id === citationNumber)
    if (source) {
      setSelectedCitation(source)
      setCitationPosition({ x: event.clientX, y: event.clientY })
      setShowCitation(true)
    }
  }

  // Process markdown text and handle citations
  const processMarkdownWithCitations = (text) => {
    if (!text) return ''
    
    // Only convert citations that have valid sources
    return text.replace(/\[(\d+)\]/g, (match, citationNumber) => {
      const citationNum = parseInt(citationNumber)
      const hasValidSource = trendingResult.sources && trendingResult.sources.find(s => s.id === citationNum)
      
      if (hasValidSource) {
        return `[[${citationNumber}]](#citation-${citationNumber})`
      } else {
        // Remove citation if no valid source exists
        return ''
      }
    })
  }

  // Generate trending narrative
   const generateTrendingNarrative = () => {
    // Gunakan analisis yang sudah diproses oleh LLM dari trendingResult.answer
    if (trendingResult.answer) {
      return trendingResult.answer;
    }
    
    // Fallback jika tidak ada analisis LLM
    if (!trendingResult.trendingItems || trendingResult.trendingItems.length === 0) {
      return 'Tidak ada data trending yang tersedia untuk periode ini.';
    }

    const query = trendingResult.query || 'topik trending';
    const dateStart = trendingResult.dateRange?.start || 'periode terkini';
    const dateEnd = trendingResult.dateRange?.end || '';
    const dateRangeText = dateEnd ? `${dateStart} hingga ${dateEnd}` : dateStart;
    
    return `# Analisis Trending Topics: ${query}\n\n` +
           `Periode: ${dateRangeText}\n\n` +
           `Sedang memproses analisis mendalam untuk topik trending ini...\n\n` +
           `Silakan tunggu beberapa saat untuk mendapatkan analisis komprehensif yang dihasilkan oleh AI.`;
  };

  // Copy text to clipboard
  const copyToClipboard = async (text, type) => {
    try {
      const currentDate = new Date().toLocaleDateString('id-ID', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
      
      let formattedText = `TRENDING TOPICS - CARA TRENDING AGENT\n`
      formattedText += `${'='.repeat(60)}\n\n`
      formattedText += `QUERY: ${query}\n`
      formattedText += `TANGGAL: ${new Date(dateRange.fromDate).toLocaleDateString('id-ID')} - ${new Date(dateRange.toDate).toLocaleDateString('id-ID')}\n`
      formattedText += `REGION: ${trendingResult.region}\n`
      formattedText += `GENERATED: ${currentDate}\n\n`
      formattedText += `${'='.repeat(60)}\n\n`
      
      if (type === 'trending-items' && trendingResult.trendingItems) {
        formattedText += `TOP 10 TRENDING TOPICS:\n\n`
        trendingResult.trendingItems.forEach((item, index) => {
          formattedText += `${index + 1}. ${item.title.toUpperCase()}\n`
          formattedText += `   Kategori: ${item.category}\n`
          formattedText += `   Sumber: ${item.domain}\n`
          formattedText += `   Deskripsi: ${item.description}\n`
          formattedText += `   URL: ${item.url}\n\n`
        })
      } else {
        formattedText += text
      }
      
      await navigator.clipboard.writeText(formattedText)
      setCopiedText(type)
      setTimeout(() => setCopiedText(''), 2000)
    } catch (err) {
      console.error('Failed to copy text: ', err)
    }
  }

  // Format date range for display
  const formatDateRange = () => {
    const fromDate = new Date(dateRange.fromDate)
    const toDate = new Date(dateRange.toDate)
    
    if (fromDate.toDateString() === toDate.toDateString()) {
      return fromDate.toLocaleDateString('id-ID', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    }
    
    return `${fromDate.toLocaleDateString('id-ID')} - ${toDate.toLocaleDateString('id-ID')}`
  }

  // Get category color for light theme
  const getCategoryColor = (category) => {
    const colors = {
      'Politik': 'bg-red-100 text-red-800',
      'Ekonomi': 'bg-green-100 text-green-800',
      'Teknologi': 'bg-blue-100 text-blue-800',
      'Olahraga': 'bg-orange-100 text-orange-800',
      'Hiburan': 'bg-purple-100 text-purple-800',
      'Kesehatan': 'bg-pink-100 text-pink-800',
      'Pendidikan': 'bg-indigo-100 text-indigo-800',
      'Sosial': 'bg-yellow-100 text-yellow-800',
      'Umum': 'bg-gray-100 text-gray-800'
    }
    return colors[category] || colors['Umum']
  }

  // Get category color for dark theme
  const getCategoryColorDark = (category) => {
    const colors = {
      'Politik': 'bg-red-500/20 text-red-400',
      'Ekonomi': 'bg-green-500/20 text-green-400',
      'Teknologi': 'bg-blue-500/20 text-blue-400',
      'Olahraga': 'bg-orange-500/20 text-orange-400',
      'Hiburan': 'bg-purple-500/20 text-purple-400',
      'Kesehatan': 'bg-pink-500/20 text-pink-400',
      'Pendidikan': 'bg-indigo-500/20 text-indigo-400',
      'Sosial & Gaya Hidup': 'bg-yellow-500/20 text-yellow-400',
      'Berita Terkini': 'bg-cyan-500/20 text-cyan-400',
      'Umum': 'bg-gray-500/20 text-gray-400'
    }
    return colors[category] || colors['Umum']
  }

  return (
    <div className="min-h-screen bg-bg-primary">
      <AgentHeader
        onBack={onBack}
        icon={TrendingUp}
        title="Trending Agent"
        query="Analisis tren terkini"
        iconColor="text-orange-500"
        iconBgColor="bg-orange-500/10"
        actions={[
          {
            icon: RefreshCw,
            label: "Cari Lagi",
            onClick: onRetry,
            variant: "primary"
          }
        ]}
      />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Query and Date Info */}
        <div className="bg-bg-tertiary rounded-xl shadow-sm border border-border-primary p-6 mb-8">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h2 className="text-2xl font-bold text-text-primary mb-2">{query}</h2>
              <div className="flex items-center space-x-4 text-sm text-text-secondary">
                <div className="flex items-center space-x-1">
                  <Calendar className="h-4 w-4" />
                  <span>{formatDateRange()}</span>
                </div>
                <div className="flex items-center space-x-1">
                  <Globe className="h-4 w-4" />
                  <span>Region: {trendingResult.region}</span>
                </div>
                <div className="flex items-center space-x-1">
                  <BarChart3 className="h-4 w-4" />
                  <span>{trendingResult.sources?.length || 0} sumber</span>
                </div>
                {trendingResult.enhancedWithLLM && (
                  <div className="flex items-center space-x-1">
                    <div className="w-2 h-2 bg-green-500 rounded-full" />
                    <span className="text-green-400">Enhanced with AI</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Trending Items */}
          <div className="lg:col-span-2 space-y-6">
            {/* Trending Topics Narrative */}
            {trendingResult.trendingItems && trendingResult.trendingItems.length > 0 && (
              <div className="bg-bg-tertiary rounded-xl shadow-sm border border-border-primary">
                <div className="p-6 border-b border-border-primary">
                  <div className="flex items-center justify-between">
                    <h3 className="text-xl font-bold text-text-primary flex items-center space-x-2">
                      <TrendingUp className="h-5 w-5 text-orange-500" />
                      <span>Trending Topics Analysis</span>
                    </h3>
                    <button
                      onClick={() => copyToClipboard(generateTrendingNarrative(), 'trending-narrative')}
                      className="flex items-center space-x-1 px-3 py-1.5 text-sm bg-bg-quaternary hover:bg-state-hover text-text-secondary rounded-lg transition-colors"
                    >
                      {copiedText === 'trending-narrative' ? (
                        <><Check className="h-4 w-4 text-green-500" /><span className="text-green-500">Disalin!</span></>
                      ) : (
                        <><Copy className="h-4 w-4" /><span>Salin</span></>
                      )}
                    </button>
                  </div>
                </div>
                <div className="p-6">
                  <div className="prose prose-invert prose-orange max-w-none">
                    <div className="text-text-secondary leading-relaxed">
                      <ReactMarkdown 
                        remarkPlugins={[remarkGfm]}
                        components={{
                          h1: ({children}) => <h1 className="text-2xl font-bold text-text-primary mb-4">{children}</h1>,
                          h2: ({children}) => <h2 className="text-xl font-semibold text-text-primary mb-3 mt-6">{children}</h2>,
                          h3: ({children}) => <h3 className="text-lg font-medium text-text-primary mb-2 mt-4">{children}</h3>,
                          p: ({children}) => <p className="text-text-secondary mb-4 leading-relaxed">{children}</p>,
                          ul: ({children}) => <ul className="list-disc list-inside mb-4 text-text-secondary space-y-1">{children}</ul>,
                          li: ({children}) => <li className="text-text-secondary">{children}</li>,
                          strong: ({children}) => <strong className="font-semibold text-text-primary">{children}</strong>,
                          em: ({children}) => <em className="italic text-text-secondary">{children}</em>,
                          hr: () => <hr className="border-border-primary my-6" />
                        }}
                      >
                        {generateTrendingNarrative()}
                      </ReactMarkdown>
                    </div>
                  </div>
                </div>
              </div>
            )}


          </div>

          {/* Right Column - Sources and Images */}
          <div className="space-y-6">
            {/* Sources */}
            {trendingResult.sources && trendingResult.sources.length > 0 && (
              <div className="bg-bg-tertiary rounded-xl shadow-sm border border-border-primary">
                <div className="p-6 border-b border-border-primary">
                  <h3 className="text-lg font-bold text-text-primary flex items-center space-x-2">
                    <Globe className="h-5 w-5 text-orange-500" />
                    <span>Sumber Referensi ({trendingResult.sources.length})</span>
                  </h3>
                </div>
                <div className="p-6">
                  <div className="space-y-4">
                    {(showAllSources ? trendingResult.sources : trendingResult.sources.slice(0, 5)).map((source, index) => (
                      <div key={source.id} className="border border-border-primary rounded-lg p-4 bg-bg-quaternary hover:bg-state-hover transition-colors">
                        <div className="flex items-start space-x-3">
                          <span className="flex items-center justify-center w-6 h-6 bg-orange-500/20 text-orange-400 rounded-full text-xs font-bold">
                            {source.id}
                          </span>
                          <div className="flex-1 min-w-0">
                            <h4 className="text-sm font-semibold text-text-primary mb-1 leading-tight">
                              {source.title}
                            </h4>
                            <p className="text-xs text-text-secondary mb-2 leading-relaxed">
                              {source.snippet}
                            </p>
                            <div className="flex items-center justify-between">
                              <span className="text-xs text-text-tertiary">{source.domain}</span>
                              <a
                                href={source.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-orange-400 hover:text-orange-300"
                              >
                                <ExternalLink className="h-3 w-3" />
                              </a>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  {trendingResult.sources.length > 5 && (
                    <button
                      onClick={() => setShowAllSources(!showAllSources)}
                      className="w-full mt-4 px-4 py-2 text-sm text-orange-400 hover:text-orange-300 font-medium"
                    >
                      {showAllSources ? 'Tampilkan Lebih Sedikit' : `Tampilkan ${trendingResult.sources.length - 5} Sumber Lainnya`}
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Images */}
            {trendingResult.images && trendingResult.images.length > 0 && (
              <div className="bg-bg-tertiary rounded-xl shadow-sm border border-border-primary">
                <div className="p-6 border-b border-border-primary">
                  <h3 className="text-lg font-bold text-text-primary flex items-center space-x-2">
                    <ImageIcon className="h-5 w-5 text-orange-500" />
                    <span>Gambar Terkait ({trendingResult.images.length})</span>
                  </h3>
                </div>
                <div className="p-6">
                  <div className="grid grid-cols-2 gap-4">
                    {(showAllImages ? trendingResult.images : trendingResult.images.slice(0, 4)).map((image, index) => (
                      <div key={image.id} className="group">
                        <div className="aspect-square bg-bg-quaternary rounded-lg overflow-hidden">
                          <img
                            src={image.url}
                            alt={image.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                            onError={(e) => {
                              e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgdmlld0JveD0iMCAwIDIwMCAyMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIyMDAiIGhlaWdodD0iMjAwIiBmaWxsPSIjMzc0MTUxIi8+CjxwYXRoIGQ9Ik04MCA4MEgxMjBWMTIwSDgwVjgwWiIgZmlsbD0iIzZCNzI4MCIvPgo8L3N2Zz4K'
                            }}
                          />
                        </div>
                        <p className="text-xs text-text-secondary mt-2 leading-tight">{image.title}</p>
                      </div>
                    ))}
                  </div>
                  {trendingResult.images.length > 4 && (
                    <button
                      onClick={() => setShowAllImages(!showAllImages)}
                      className="w-full mt-4 px-4 py-2 text-sm text-orange-400 hover:text-orange-300 font-medium"
                    >
                      {showAllImages ? 'Tampilkan Lebih Sedikit' : `Tampilkan ${trendingResult.images.length - 4} Gambar Lainnya`}
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Search More */}
            <div className="bg-gradient-to-r from-orange-500 to-red-500 rounded-xl shadow-sm text-white p-6">
              <h3 className="text-lg font-bold mb-2">Cari Trending Lainnya</h3>
              <p className="text-orange-100 text-sm mb-4">
                Temukan topik trending lainnya dengan tanggal dan kategori yang berbeda
              </p>
              <button
                onClick={handleSearchMore}
                className="w-full bg-white text-orange-600 font-medium py-2 px-4 rounded-lg hover:bg-orange-50 transition-colors"
              >
                Mulai Pencarian Baru
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Citation Popup */}
      {showCitation && selectedCitation && (
        <CitationPopup
          citation={selectedCitation}
          position={citationPosition}
          onClose={() => setShowCitation(false)}
        />
      )}
    </div>
  )
}

TrendingResultPage.propTypes = {
  query: PropTypes.string.isRequired,
  trendingResult: PropTypes.object.isRequired,
  dateRange: PropTypes.object.isRequired,
  onBack: PropTypes.func.isRequired,
  onRetry: PropTypes.func.isRequired
}

export default TrendingResultPage